'use client';

import { useState, useCallback } from 'react';
import { motion } from 'motion/react';
import { useLocale } from './LocaleProvider';
import { dict, t } from '@/lib/i18n';

const BRANDS = [
  { id: 'hermes', name: 'Hermès', rate: 0.15 },
  { id: 'rolex', name: 'Rolex', rate: 0.12 },
  { id: 'chanel', name: 'Chanel', rate: 0.12 },
  { id: 'patek', name: 'Patek Philippe', rate: 0.18 },
  { id: 'lv', name: 'Louis Vuitton', rate: 0.06 },
  { id: 'dior', name: 'Dior', rate: 0.04 },
  { id: 'gucci', name: 'Gucci', rate: -0.05 },
] as const;

function formatAED(n: number): string {
  return `AED ${Math.round(n).toLocaleString()}`;
}

export function InvestmentCalc() {
  const { locale } = useLocale();
  const [brandIdx, setBrandIdx] = useState(0);
  const [price, setPrice] = useState(50000);

  const brand = BRANDS[brandIdx]!;
  const projections = [1, 3, 5].map((years) => ({
    years,
    value: price * Math.pow(1 + brand.rate, years),
    gain: price * Math.pow(1 + brand.rate, years) - price,
  }));

  const handleBrand = useCallback((i: number) => setBrandIdx(i), []);
  const handlePrice = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setPrice(Number(e.target.value));
  }, []);

  return (
    <section className="relative py-14 md:py-28 border-t border-white/5 bg-gradient-to-b from-ink-900 to-ink-800/50">
      <div className="mx-auto max-w-5xl px-6">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-gold-soft font-semibold mb-4">
            <span className="w-1 h-1 rounded-full bg-gold-soft" />
            {t(dict.calculator.eyebrow, locale)}
          </div>
          <h2 className="font-serif text-display-sm text-cream leading-[0.98]">
            {t(dict.calculator.heading, locale)}
          </h2>
          <p className="mt-4 text-fog-500 text-sm max-w-lg mx-auto">
            {t(dict.calculator.disclaimer, locale)}
          </p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.6 }}
          className="grid lg:grid-cols-2 gap-8"
        >
          {/* Controls */}
          <div className="rounded-luxe bg-ink-800/80 border border-white/10 p-6 sm:p-8">
            {/* Brand selector */}
            <div className="mb-8">
              <label className="text-xs tracking-[0.2em] uppercase text-fog-500 font-semibold block mb-3">
                {t(dict.calculator.brandLabel, locale)}
              </label>
              <div className="flex flex-wrap gap-2">
                {BRANDS.map((b, i) => (
                  <button
                    key={b.id}
                    onClick={() => handleBrand(i)}
                    className={`px-3.5 py-2 rounded-full text-sm transition ${
                      i === brandIdx
                        ? 'bg-gold/20 border border-gold/40 text-gold-soft font-semibold'
                        : 'bg-white/5 border border-white/10 text-fog-300 hover:border-gold/20'
                    }`}
                  >
                    {b.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Price slider */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="text-xs tracking-[0.2em] uppercase text-fog-500 font-semibold">
                  {t(dict.calculator.priceLabel, locale)}
                </label>
                <span className="text-gold-soft font-mono font-semibold text-lg">
                  {formatAED(price)}
                </span>
              </div>
              <input
                type="range"
                min={5000}
                max={500000}
                step={5000}
                value={price}
                onChange={handlePrice}
                className="w-full h-2 rounded-full appearance-none bg-white/10 accent-gold cursor-pointer
                  [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:h-5
                  [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-gold [&::-webkit-slider-thumb]:shadow-glow
                  [&::-webkit-slider-thumb]:cursor-pointer"
              />
              <div className="flex justify-between text-xs text-fog-700 mt-1">
                <span>AED 5K</span>
                <span>AED 500K</span>
              </div>
            </div>

            {/* Annual rate */}
            <div className="mt-6 flex items-center gap-3 p-3 rounded-lg bg-white/[0.03] border border-white/5">
              <span className="text-lg">{brand.rate > 0 ? '📈' : '📉'}</span>
              <div>
                <div className="text-xs text-fog-500">
                  {t(dict.calculator.annualRate, locale)}
                </div>
                <div className={`text-sm font-mono font-semibold ${brand.rate > 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                  {brand.rate > 0 ? '+' : ''}{(brand.rate * 100).toFixed(0)}% / {t(dict.calculator.year, locale)}
                </div>
              </div>
            </div>
          </div>

          {/* Projections */}
          <div className="rounded-luxe bg-ink-800/80 border border-white/10 p-6 sm:p-8">
            <div className="text-xs tracking-[0.2em] uppercase text-fog-500 font-semibold mb-6">
              {t(dict.calculator.projectionLabel, locale)}
            </div>
            <div className="space-y-4">
              {projections.map(({ years, value, gain }) => {
                const pct = ((value - price) / price) * 100;
                return (
                  <div key={years} className="p-4 rounded-lg bg-white/[0.03] border border-white/5">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-fog-300 text-sm">
                        {years} {years === 1 ? t(dict.calculator.year, locale) : t(dict.calculator.years, locale)}
                      </span>
                      <span className={`text-xs font-mono font-semibold px-2 py-0.5 rounded ${
                        gain >= 0 ? 'bg-emerald-950 text-emerald-400' : 'bg-red-950 text-red-400'
                      }`}>
                        {gain >= 0 ? '+' : ''}{pct.toFixed(0)}%
                      </span>
                    </div>
                    <div className="font-serif text-2xl text-fog-100 font-semibold">
                      {formatAED(value)}
                    </div>
                    <div className={`text-xs mt-1 ${gain >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                      {gain >= 0 ? '+' : ''}{formatAED(gain)} {t(dict.calculator.vsToday, locale)}
                    </div>
                  </div>
                );
              })}
            </div>

            <p className="mt-4 text-xs text-fog-700 leading-relaxed">
              {t(dict.calculator.footnote, locale)}
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
